import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { isTask2MaintenanceEnabled } from "@/config/maintenance";
import Task2MaintenanceNotice from "@/components/task2/Task2MaintenanceNotice";

type LevelOption = {
  level: string;
  label: string;
  helper: string;
};

const levelOptions: LevelOption[] = [
  {
    level: "B1",
    label: "Intermediate",
    helper: "Manage everyday life and basic work or study tasks.",
  },
  {
    level: "B2",
    label: "Upper-intermediate",
    helper: "Confident in daily life; common target for Canadian PR.",
  },
  {
    level: "C1",
    label: "Advanced",
    helper: "Comfortable in complex study, work, and discussions.",
  },
  {
    level: "C2",
    label: "Proficient",
    helper: "Near-native precision, flexible in any situation.",
  },
];

const ExamLevelSelection = () => {
  const [selectedLevel, setSelectedLevel] = useState<string>("");
  const navigate = useNavigate();

  // ✅ Load saved goal level from localStorage on mount
  useEffect(() => {
    const savedLevel = localStorage.getItem("goal_level");
    if (savedLevel) {
      setSelectedLevel(savedLevel);
    }
  }, []);

  useEffect(() => {
  window.scrollTo(0, 0);
}, []);

  // ✅ Handle level selection and save to localStorage
  const handleLevelSelect = (level: string) => {
    setSelectedLevel(level);
    localStorage.setItem("goal_level", level);
  };

  // ✅ Navigate to mic-test with state + ensure localStorage has the level
  const handleContinue = () => {
    if (isTask2MaintenanceEnabled()) {
      return;
    }
    if (selectedLevel) {
      localStorage.setItem("goal_level", selectedLevel);
      navigate("/mic-test", { state: { goal_level: selectedLevel, examMode: "full" } });
    } else {
      alert("Please select a goal level before continuing.");
    }
  };

  return (
    <div className="flex items-center justify-center px-4 sm:px-6 md:px-8 py-10 sm:py-12 md:py-16">
      <div className="w-full max-w-[1140px]">
        {/* Header */}
        <div className="max-w-[680px] mx-auto text-center mb-10 px-2">
          <h1 className="text-[22px] sm:text-[28px] leading-[32px] sm:leading-[40px] font-bold text-[#1C1F26] font-inter mb-4">
            Before we start your exam
          </h1>
          <p className="text-[16px] sm:text-[18px] leading-[24px] sm:leading-[28px] font-inter text-[#4B5563]">
            Let’s personalize your feedback to help you reach your goal.
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-[#E5E7EB] p-6 sm:p-8 md:p-10 max-w-[680px] mx-auto">
          {/* Card Header */}
          <h2 className="text-[18px] sm:text-[22px] leading-[26px] sm:leading-[32px] font-inter font-semibold text-[#1C1F26] mb-2">
            Which level do you want to reach?
          </h2>
          <p className="text-[14px] sm:text-[16px] leading-[22px] sm:leading-[26px] font-inter text-[#6B7280] mb-8 sm:mb-10">
            This won’t change your exam difficulty, it just helps us give the
            right tips.
          </p>

          {/* Level Options */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-10 sm:mb-12">
            {levelOptions.map((option) => (
              <button
                key={option.level}
                type="button"
                onClick={() => handleLevelSelect(option.level)}
                className={`w-full min-h-[112px] sm:min-h-[128px] rounded-[12px] border-2 box-border p-4 sm:p-6 transition-all duration-200 text-left flex flex-col justify-start ${
                  selectedLevel === option.level
                    ? "border-[#005FCC] bg-[#F0F7FF] shadow-[0px_8px_20px_rgba(0,0,0,0.08)]"
                    : "border-[#E5E7EB] bg-white hover:border-[#005FCC] hover:shadow-[0px_8px_20px_rgba(0,0,0,0.06)]"
                }`}
              >
                <div className="text-[20px] sm:text-[24px] leading-[28px] sm:leading-[36px] font-inter font-bold text-[#1C1F26] mb-2">
                  {option.level}
                </div>
                <div className="text-[13px] sm:text-[14px] leading-[18px] sm:leading-[20px] font-inter text-[#6B7280]">
                  <span className="font-semibold">{option.label}</span>
                  <span className="font-normal"> · {option.helper}</span>
                </div>
              </button>
            ))}
          </div>

          {isTask2MaintenanceEnabled() ? (
            <Task2MaintenanceNotice variant="fullExam" className="mb-6" />
          ) : null}

          {/* CTA Section */}
          <div className="space-y-4">
            <Button
              size="lg"
              type="button"
              disabled={isTask2MaintenanceEnabled()}
              className="cursor-pointer w-full h-[48px] sm:h-[56px] px-2 border-0 box-border rounded-xl shadow-[0px_8px_20px_rgba(0,0,0,0.08)] bg-[#005FCC] text-white text-[15px] sm:text-[16px] leading-[22px] sm:leading-[24px] font-inter font-semibold outline-none hover:bg-[#0049A8] disabled:opacity-50"
              onClick={handleContinue}
            >
              {isTask2MaintenanceEnabled()
                ? "Temporarily unavailable"
                : "Continue to Exam"}
            </Button>

            <div className="text-center">
              <button
                type="button"
                className="text-[13px] sm:text-[14px] leading-[20px] sm:leading-[21px] font-inter text-[#6B7280] hover:underline"
                onClick={() => navigate("/dashboard")}
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamLevelSelection;
